<template>
  <!-- Origem Flow: recorte da captura de tela antes de anexar no comentário.
       Arrastar seleciona a área; sem seleção, vai a imagem inteira. -->
  <Teleport to="body">
    <div class="screenshot-crop" @mousedown.stop @click.stop>
      <div class="screenshot-crop__stage">
        <div
          ref="frame"
          class="screenshot-crop__frame"
          @pointerdown="startSelection"
          @pointermove="moveSelection"
          @pointerup="endSelection"
          @pointercancel="endSelection"
        >
          <canvas ref="canvas" class="screenshot-crop__image"></canvas>
          <div
            v-if="selection"
            class="screenshot-crop__selection"
            :style="selectionStyle"
          >
            <span class="screenshot-crop__size">{{ selectionSize }}</span>
          </div>
        </div>
      </div>
      <div class="screenshot-crop__bar">
        <span class="screenshot-crop__hint">{{
          $t('screenshotCropOverlay.hint')
        }}</span>
        <a
          v-if="selection"
          class="screenshot-crop__button"
          @click.prevent="selection = null"
        >
          {{ $t('screenshotCropOverlay.clear') }}
        </a>
        <a class="screenshot-crop__button" @click.prevent="$emit('cancel')">
          {{ $t('screenshotCropOverlay.cancel') }}
        </a>
        <a
          class="screenshot-crop__button screenshot-crop__button--primary"
          @click.prevent="confirm()"
        >
          {{
            selection
              ? $t('screenshotCropOverlay.attachSelection')
              : $t('screenshotCropOverlay.attachAll')
          }}
        </a>
      </div>
    </div>
  </Teleport>
</template>

<script>
import {
  normalizeSelection,
  selectionToSourceRect,
} from '@baserow_premium/utils/screenCapture'

export default {
  name: 'ScreenshotCropOverlay',
  props: {
    // Canvas com o quadro capturado, em resolução nativa.
    source: {
      type: Object,
      required: true,
    },
  },
  emits: ['confirm', 'cancel'],
  data() {
    return {
      // Seleção em frações (0–1) da imagem, para sobreviver a um resize.
      selection: null,
      dragStart: null,
    }
  },
  computed: {
    selectionStyle() {
      const { left, top, width, height } = this.selection
      return {
        left: `${left * 100}%`,
        top: `${top * 100}%`,
        width: `${width * 100}%`,
        height: `${height * 100}%`,
      }
    },
    selectionSize() {
      const rect = selectionToSourceRect(
        this.selection,
        this.source.width,
        this.source.height
      )
      return `${rect.width} × ${rect.height}`
    },
  },
  mounted() {
    const canvas = this.$refs.canvas
    canvas.width = this.source.width
    canvas.height = this.source.height
    canvas.getContext('2d').drawImage(this.source, 0, 0)
    // Tira o foco do editor para a digitação não cair no comentário.
    document.activeElement?.blur?.()
    // Em captura, antes dos atalhos do modal da linha (Esc fecha o modal no
    // keyup) e do editor (Enter envia o comentário).
    window.addEventListener('keydown', this.onKeydown, true)
    window.addEventListener('keyup', this.onKeyup, true)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown, true)
    window.removeEventListener('keyup', this.onKeyup, true)
  },
  methods: {
    pointFromEvent(event) {
      const rect = this.$refs.frame.getBoundingClientRect()
      return {
        x: (event.clientX - rect.left) / rect.width,
        y: (event.clientY - rect.top) / rect.height,
      }
    },
    startSelection(event) {
      if (event.button !== 0) {
        return
      }
      event.preventDefault()
      this.$refs.frame.setPointerCapture?.(event.pointerId)
      this.dragStart = this.pointFromEvent(event)
      this.selection = null
    },
    moveSelection(event) {
      if (!this.dragStart) {
        return
      }
      this.selection = normalizeSelection(
        this.dragStart,
        this.pointFromEvent(event)
      )
    },
    endSelection(event) {
      if (!this.dragStart) {
        return
      }
      this.moveSelection(event)
      this.dragStart = null
    },
    confirm() {
      this.$emit('confirm', this.selection)
    },
    onKeydown(event) {
      if (event.key !== 'Enter' && event.key !== 'Escape') {
        return
      }
      event.preventDefault()
      event.stopPropagation()
      if (event.key === 'Enter' && !event.repeat) {
        this.confirm()
      }
    },
    onKeyup(event) {
      if (event.key !== 'Escape') {
        return
      }
      // O cancelamento fica no keyup: se saísse no keydown, o keyup seguinte
      // já encontraria este componente desmontado e fecharia o modal da linha.
      event.stopPropagation()
      this.$emit('cancel')
    },
  },
}
</script>
